'use client';

import {
  cn,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useFieldContext,
} from '@ctct/summit-core';
import { useTranslations } from 'next-intl';
import { type ChangeEvent, type FocusEvent, type Ref, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { type InputOpts } from '../../types';

const HHMM_PATTERN = /^(\d{1,2}):(\d{2})$/;
const DIGITS_PATTERN = /^\d*$/;

type Period = 'AM' | 'PM';

interface TimeParts {
  hour: string;
  minute: string;
  period: Period;
}

const EMPTY_PARTS: TimeParts = { hour: '', minute: '', period: 'AM' };

// Form values stay in 24-hour "HH:mm" so server-side and luxon parsing logic
// in callers don't need to change. The 12-hour split is purely a UI concern,
// chosen because native <input type="time"> defers to the OS locale (and
// ignores the `lang` attribute in every major browser).
const splitTo12Hour = (twentyFourHour: string): TimeParts => {
  const match = HHMM_PATTERN.exec(twentyFourHour.trim());
  if (!match) return EMPTY_PARTS;

  const h24 = Number(match[1]);
  const m = Number(match[2]);
  const period: Period = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 || 12;

  return {
    hour: String(h12),
    minute: String(m).padStart(2, '0'),
    period,
  };
};

const partsTo24Hour = ({ hour, minute, period }: TimeParts): string => {
  if (!hour || !minute) return '';
  const hNum = Number(hour);
  const mNum = Number(minute);
  if (!Number.isInteger(hNum) || !Number.isInteger(mNum)) return '';
  if (hNum < 1 || hNum > 12 || mNum < 0 || mNum > 59) return '';

  let h24 = hNum % 12;
  if (period === 'PM') h24 += 12;
  return `${String(h24).padStart(2, '0')}:${String(mNum).padStart(2, '0')}`;
};

interface TwelveHourTimeControlProps {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  hasError: boolean;
  isDisabled: boolean;
  triggerId?: string;
  labelId?: string;
  name?: string;
  className?: string;
  inputClassName?: string;
  ariaLabel?: string;
  hourRef?: Ref<HTMLInputElement>;
}

const TwelveHourTimeControl = ({
  value,
  onChange,
  onBlur,
  hasError,
  isDisabled,
  triggerId,
  labelId,
  name,
  className,
  inputClassName,
  ariaLabel,
  hourRef,
}: TwelveHourTimeControlProps) => {
  const t = useTranslations('UiForm');
  const [parts, setParts] = useState<TimeParts>(() => splitTo12Hour(value));
  // Track the form value we last pushed up so the sync effect can distinguish
  // our own commits (which must not clobber transient invalid states like an
  // out-of-range hour) from genuine external updates (suggested-send-times,
  // form resets, etc. — where we DO want to re-derive the segmented parts).
  const lastCommittedRef = useRef(value);

  useEffect(() => {
    if (value === lastCommittedRef.current) return;
    lastCommittedRef.current = value;
    setParts(splitTo12Hour(value));
  }, [value]);

  const commitParts = (next: TimeParts) => {
    setParts(next);
    const committed = partsTo24Hour(next);
    lastCommittedRef.current = committed;
    onChange(committed);
  };

  const handleHourChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    if (!DIGITS_PATTERN.test(raw) || raw.length > 2) return;
    commitParts({ ...parts, hour: raw });
  };

  const handleMinuteChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    if (!DIGITS_PATTERN.test(raw) || raw.length > 2) return;
    commitParts({ ...parts, minute: raw });
  };

  const handleHourBlur = () => {
    if (parts.hour) {
      const clamped = Math.min(12, Math.max(1, Number(parts.hour) || 1));
      commitParts({ ...parts, hour: String(clamped) });
    }
    onBlur();
  };

  const handleMinuteBlur = () => {
    if (parts.minute) {
      const clamped = Math.min(59, Math.max(0, Number(parts.minute) || 0));
      commitParts({ ...parts, minute: String(clamped).padStart(2, '0') });
    }
    onBlur();
  };

  const handlePeriodChange = (period: string) => {
    commitParts({ ...parts, period: period as Period });
  };

  // Native <input type="time"> auto-selects each segment on focus so typing
  // replaces it. With a plain text input + maxLength=2, typing after an
  // existing digit produces a two-digit value the user is then stuck with
  // (e.g. starting at "8" and typing "1" yields "81" with no room for "0").
  const handleFocusSelect = (event: FocusEvent<HTMLInputElement>) => {
    event.currentTarget.select();
  };

  const hourLabel = ariaLabel
    ? `${ariaLabel} ${t('shared.time.hourAriaLabel')}`
    : t('shared.time.hourAriaLabel');
  const minuteLabel = ariaLabel
    ? `${ariaLabel} ${t('shared.time.minuteAriaLabel')}`
    : t('shared.time.minuteAriaLabel');
  const periodLabel = ariaLabel
    ? `${ariaLabel} ${t('shared.time.periodAriaLabel')}`
    : t('shared.time.periodAriaLabel');

  return (
    <div
      className={cn('r:flex r:w-full r:items-center r:gap-2', className)}
      data-testid="time-picker-input"
    >
      <div className="r:flex-1 r:min-w-0">
        <Input
          ref={hourRef}
          id={triggerId}
          name={name ? `${name}-hour` : undefined}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={2}
          placeholder={t('shared.time.hourPlaceholder')}
          value={parts.hour}
          onChange={handleHourChange}
          onBlur={handleHourBlur}
          onFocus={handleFocusSelect}
          disabled={isDisabled}
          hasError={hasError}
          shouldHideStatusIcon
          aria-labelledby={ariaLabel ? undefined : labelId}
          aria-label={hourLabel}
          className={cn('r:text-center', inputClassName)}
        />
      </div>
      <span aria-hidden="true" className="r:text-(--text)">
        :
      </span>
      <div className="r:flex-1 r:min-w-0">
        <Input
          name={name ? `${name}-minute` : undefined}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={2}
          placeholder={t('shared.time.minutePlaceholder')}
          value={parts.minute}
          onChange={handleMinuteChange}
          onBlur={handleMinuteBlur}
          onFocus={handleFocusSelect}
          disabled={isDisabled}
          hasError={hasError}
          shouldHideStatusIcon
          aria-label={minuteLabel}
          className={cn('r:text-center', inputClassName)}
        />
      </div>
      <Select value={parts.period} onValueChange={handlePeriodChange} disabled={isDisabled}>
        <SelectTrigger
          aria-label={periodLabel}
          hasError={hasError}
          className={cn('r:flex-1 r:min-w-0', inputClassName)}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="AM">{t('shared.time.am')}</SelectItem>
          <SelectItem value="PM">{t('shared.time.pm')}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

interface TimePickerInputProps extends InputOpts {
  /** Override the id assigned to the hour input. Falls back to the surrounding `<Field>` id. */
  id?: string;
  /** Render the inputs in their error state. */
  hasError?: boolean;
  /** Called after the user changes the time. Receives the 24-hour "HH:mm" value (or `''`). */
  onValueChange?: (value: string) => void;
  /** Classes applied to the wrapping flex container. */
  className?: string;
  /** Classes applied to each individual input and the period select trigger. */
  inputClassName?: string;
  /** Aria label for the time group when no associated `<Label>` is present. */
  'aria-label'?: string;
}

/**
 * A react-hook-form-aware time picker that always renders a 12-hour UI
 * (hour input + minute input + AM/PM select) while keeping the underlying
 * form value in 24-hour "HH:mm" format for parity with `<input type="time">`.
 */
const TimePickerInput = ({
  formKey,
  registerOpts,
  id,
  hasError,
  onValueChange,
  className,
  inputClassName,
  'aria-label': ariaLabel,
}: TimePickerInputProps) => {
  const { control } = useFormContext();
  const { inputId, labelId, state } = useFieldContext();

  const hasEffectiveError = hasError ?? state === 'error';
  const isDisabled = state === 'disabled';
  const triggerId = id ?? inputId;

  return (
    <Controller
      control={control}
      name={formKey}
      rules={registerOpts}
      render={({ field }) => {
        const handleChange = (value: string) => {
          field.onChange(value);
          onValueChange?.(value);
        };
        const handleBlur = () => {
          field.onBlur();
        };

        return (
          <TwelveHourTimeControl
            value={String(field.value ?? '')}
            onChange={handleChange}
            onBlur={handleBlur}
            hasError={hasEffectiveError}
            isDisabled={isDisabled}
            triggerId={triggerId}
            labelId={labelId}
            name={field.name}
            className={className}
            inputClassName={inputClassName}
            ariaLabel={ariaLabel}
            hourRef={field.ref}
          />
        );
      }}
    />
  );
};

export { TimePickerInput, type TimePickerInputProps };
